import {
  type Configuration,
  type ConfigurationRequestDto,
  type TypeGroupPair,
  type UserGroup,
  type UserGroupDto,
  type UserType,
  type UserTypeDto,
  type Value,
  type ValueDto
} from "./types";

export const mapUserGroupsDtoToUserGroups = (
  userGroupsDto: UserGroupDto[],
  parents: UserGroupDto[] = []
): UserGroup[] =>
  userGroupsDto?.reduce<UserGroup[]>(
    (userGroups, userGroupDto) => [
      ...userGroups,
      mapValueDtoToValue(userGroupDto, parents),
      ...mapUserGroupsDtoToUserGroups(userGroupDto.children, [...parents, userGroupDto])
    ],
    []
  ) ?? [];

export const mapUserTypesDtoToUserTypes = (userTypesDto: UserTypeDto[]): UserType[] =>
  userTypesDto?.map(dto => mapValueDtoToValue(dto)) ?? [];

const mapValueDtoToValue = (valueDto: ValueDto, parents: UserGroupDto[] = []): Value => ({
  code: valueDto.v,
  description: valueDto.d,
  parents: parents.map(parent => mapValueDtoToValue(parent))
});

const mapTypeGroupPairs = (data: Configuration) =>
  Object.entries(data)
    .map(entry => {
      if (entry[0].startsWith("type_") && entry[1]) {
        return {
          typeId: Number.parseInt(entry[0].split("_")[1]),
          groupId: Number.parseInt(entry[0].split("_")[3])
        };
      } else {
        return undefined;
      }
    })
    .filter(entry => entry);

export const isPairUnavailable = (
  unavailableTypeGroupPairs: TypeGroupPair[],
  userType: UserType,
  userGroup: UserGroup
) =>
  unavailableTypeGroupPairs.some(
    pair => pair.typeId === userType.code && pair.groupId === userGroup.code
  );

export const getFullPath = (value?: Value): string =>
  value
    ? `${value.parents?.map(parent => `${parent.description} - `).join("") ?? ""}${
        value.description
      }`
    : "";

export const getDistinct = (values: Value[]) => {
  const allCodes = values.map(value => value.code);
  return values.filter(({code}, index) => !allCodes.includes(code, index + 1));
};

export const isAnyPairAvailable = (
  unavailableTypeGroupPairs: TypeGroupPair[] = [],
  userTypes: UserType[] = [],
  userGroups: UserGroup[] = []
): boolean => unavailableTypeGroupPairs.length < userTypes.length * userGroups.length;

export const mapConfigurationToDto = (data: Configuration): ConfigurationRequestDto => ({
  name: data.name?.trim(),
  description: data.description?.trim(),
  typeGroupPairs: mapTypeGroupPairs(data)
});
